import { Injectable, signal, inject, NgZone } from '@angular/core';
import { Toast, ToastConfig, ToastType } from './toast.types';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  readonly toasts = signal<Toast[]>([]);

  private readonly ngZone = inject(NgZone);

  /**
   * Muestra un nuevo toast con la configuración dada.
   */
  show(config: ToastConfig) {
    const id = crypto.randomUUID();
    const toast: Toast = {
      ...config,
      id,
      type: config.type || 'info', // Default type
      duration: config.duration ?? 5000 // Default duration
    };

    this.toasts.update(current => [...current, toast]);

    if (toast.duration && toast.duration > 0) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => this.remove(id));
        }, toast.duration);
      });
    }
  }

  /**
   * Cierra un toast específico por ID
   */
  remove(id: string) {
    // 1. Marcar como cerrando para disparar la animación de salida
    this.toasts.update(current =>
      current.map(t => t.id === id ? { ...t, state: 'closing' } : t)
    );

    // 2. Esperar a que termine la animación (400ms) y eliminar del array
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          this.toasts.update(current => current.filter(t => t.id !== id));
        });
      }, 400);
    });
  }

  /**
   * Cierra todos los toasts
   */
  clear() {
    this.toasts.set([]);
  }

  // --- Helpers rápidos ---

  success(title: string, message: string = '', duration: number = 5000) {
    this.show({ title, message, type: 'success', duration });
  }

  error(title: string, message: string = '', duration: number = 5000) {
    this.show({ title, message, type: 'error', duration });
  }

  warning(title: string, message: string = '', duration: number = 5000) {
    this.show({ title, message, type: 'warning', duration });
  }

  info(title: string, message: string = '', duration: number = 5000) {
    this.show({ title, message, type: 'info', duration });
  }

  custom(title: string, message: string, color: string, duration: number = 5000) {
    this.show({ title, message, type: 'custom', customColor: color, duration });
  }
}
